"use client";

import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Assistant } from "@/components/chat/AssistantSelector";
import ChatInput, { ChatInputHandle } from "@/components/chat/ChatInput";
import SkillCard, {SkillCardSkeleton} from "@/components/chat/SkillCard";
import Icon, {ICON_SIZES} from "@/components/library/Icon";
import useMediaQuery from "@/hooks/useMediaQuery";
import {AgentSkill} from "@/types/skills";
import {useChatSkills} from "./useChatSkills";
import FileDropZone from "@/components/common/FileDropZone";
import BrandImage from "@/components/common/BrandImage";
import {DEFAULT_BRAND} from "@/enums/brands";
import GailGptWelcomeTour from "@/components/chat/GailGptWelcomeTour";
import type {UserAcknowledgementStatus} from "@/utils/acknowledgements/userAcknowledgements";

const MOBILE_PAGE_SIZE = 3;
const DESKTOP_PAGE_SIZE = 6;

interface CarouselArrowButtonProps {
  direction: "prev" | "next";
  disabled: boolean;
  onClick: () => void;
  ariaLabel: string;
}

const CarouselArrowButton: React.FC<CarouselArrowButtonProps> = ({
  direction,
  disabled,
  onClick,
  ariaLabel,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={ariaLabel}
    className={clsx(
      "flex items-center justify-center w-8 h-8 rounded-full",
      "border border-gpt-border bg-transparent text-gpt-text-secondary",
      "transition-all duration-200",
      "hover:border-gpt-accent hover:text-900 hover:bg-gpt-card",
      "focus:outline-none focus:ring-2 focus:ring-[var(--gpt-accent-ring)]",
      "disabled:opacity-30 disabled:cursor-not-allowed",
      "disabled:hover:border-gpt-border disabled:hover:text-gpt-text-secondary disabled:hover:bg-transparent",
    )}
  >
    <Icon
      icon={direction === "prev" ? "chevron-left" : "chevron-right"}
      size={ICON_SIZES.SM}
    />
  </button>
);

interface WelcomeScreenProps {
  greeting?: string;
  inputPlaceholder?: string;
  inputValue: string;
  onInputChange: (value: string) => void;
  onSendMessage: (message?: string) => void;
  className?: string;
  isBannerActive?: boolean;
  loading?: boolean;
  assistants?: Assistant[];
  selectedAssistant?: Assistant;
  onAssistantChange?: (assistant: Assistant) => void;
  onFilesSelected?: (files: File[]) => Promise<void> | void;
  attachedFiles?: File[];
  onFilesChange?: (files: File[]) => void;
  fileUploadConfig?: {
    enabled: boolean;
    acceptedFileTypes: string;
    maxFileSize: number;
    maxFileCount: number;
    totalMaxFileSize: number;
  };
  onInvalidFiles?: (rejectedFiles: { file: File; reason: string }[]) => void;
  fileCompatibilityIssue?: boolean;
  fileCompatibilityTooltip?: string;
  shouldShowOnboarding?: boolean;
  onOnboardingComplete?: (status: UserAcknowledgementStatus) => void;
  canEditModel?: boolean;
  onModelChange?: (assistantId: string, newModel: string) => void;
  isModelSaving?: boolean;
  isLoadingAssistants?: boolean;
  brand?: string;
  canAccessGails?: boolean;
  // null === skills are still loading (renders skeleton placeholders).
  // undefined or [] === no API skills, fall back to the hardcoded set.
  skills?: AgentSkill[] | null;
}

const WelcomeScreen: React.FC<WelcomeScreenProps> = ({
  greeting,
  inputPlaceholder,
  inputValue,
  onInputChange,
  onSendMessage,
  className,
  loading = false,
  assistants,
  selectedAssistant,
  onAssistantChange,
  onFilesSelected,
  attachedFiles = [],
  onFilesChange,
  fileUploadConfig,
  onInvalidFiles,
  fileCompatibilityIssue,
  fileCompatibilityTooltip,
  shouldShowOnboarding = false,
  onOnboardingComplete,
  canEditModel,
  onModelChange,
  isModelSaving,
  isLoadingAssistants = false,
  brand = DEFAULT_BRAND,
  canAccessGails = false,
  skills: apiSkills,
}) => {
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [composerExpanded, setComposerExpanded] = useState(false);
  const chatInputRef = useRef<ChatInputHandle>(null);
  const { t: askT } = useTranslation("ask");

  const displaySkills = useChatSkills(apiSkills);

  // Carousel pagination: 3 per page on mobile, 6 on desktop. lazyInit reads
  // window.matchMedia synchronously on first render so we don't briefly render
  // mobile pagination on desktop while the media-query effect catches up. Safe
  // because the parent (WelcomePageContainer) gates this whole subtree behind
  // a post-mount boundary, so SSR never evaluates this.
  const isSmUp = useMediaQuery("(min-width: 640px)", { lazyInit: true });
  const isLoadingSkills = apiSkills === null;
  const pageSize = isSmUp ? DESKTOP_PAGE_SIZE : MOBILE_PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(displaySkills.length / pageSize));
  const [page, setPage] = useState(0);

  // Keep the page index in range if the breakpoint or skill count changes.
  useEffect(() => {
     if (page > totalPages - 1) setPage(Math.max(0, totalPages - 1));
  }, [page, totalPages]);

  const visibleSkills = useMemo(
     () => displaySkills.slice(page * pageSize, page * pageSize + pageSize),
     [displaySkills, page, pageSize],
  );
  const showPagination = !isLoadingSkills && totalPages > 1;
  const goPrev = useCallback(() => setPage((p) => Math.max(0, p - 1)), []);
  const goNext = useCallback(
     () => setPage((p) => Math.min(totalPages - 1, p + 1)),
     [totalPages],
  );

  // Reset button disabled state when loading stops
  useEffect(() => {
    if (!loading && buttonDisabled) {
      setButtonDisabled(false);
    }
  }, [loading, buttonDisabled]);

  const showAssistantSelector =
    !!assistants && !!selectedAssistant && !!onAssistantChange;
  const tourVisibility = useMemo(
    () => ({
      showAssistantSelector,
      showGailsNav: canAccessGails,
      showHistoryNav: !canAccessGails,
    }),
    [canAccessGails, showAssistantSelector]
  );

  const handleSubmit = () => {
    setButtonDisabled(true);
    onSendMessage();
  };

  const handleSuggestionClick = (slug: string) => {
    onInputChange(`/${slug} `);
    chatInputRef.current?.focusEnd();
  };

  return (
    <GailGptWelcomeTour
      shouldStart={shouldShowOnboarding && !!assistants?.length}
      onFinish={onOnboardingComplete}
      visibility={tourVisibility}
    >
      <FileDropZone
        onFilesSelected={onFilesSelected ?? (() => {})}
        disabled={loading || buttonDisabled || !fileUploadConfig?.enabled}
        acceptedFileTypes={fileUploadConfig?.acceptedFileTypes ?? ""}
        allowMultiple={(fileUploadConfig?.maxFileCount ?? 1) > 1}
        fileUploadConfig={fileUploadConfig}
        assistantModel={selectedAssistant?.model}
        onInvalidFiles={onInvalidFiles}
        showAlerts={true}
        className={clsx(
          "flex min-h-[var(--welcome-viewport-height,100%)] w-full flex-col font-inter text-900",
          className
        )}
      >
        <div className="flex w-full flex-1 flex-col items-center px-6">
            <div className="hidden md:block md:flex-1" />
            {!composerExpanded && (
              <h1 className="mb-8 mt-8 font-normal text-900 text-[36px] text-center flex items-center justify-center gap-3 flex-shrink-0 font-source-serif tracking-[-0.5px]">
                <BrandImage
                  brand={brand}
                  className="inline-block"
                  height={36}
                  variant="small"
                />
                {greeting}
              </h1>
            )}

            <div className="mb-6 w-full max-w-[720px] shrink-0">
              <div data-tour="chat-input">
                <ChatInput
                  ref={chatInputRef}
                  skills={displaySkills}
                  isLoadingSkills={isLoadingSkills}
                  value={inputValue}
                  onChange={onInputChange}
                  onSubmit={handleSubmit}
                  placeholder={inputPlaceholder}
                  variant="large"
                  className="w-full"
                  assistants={assistants}
                  selectedAssistant={selectedAssistant}
                  onAssistantChange={onAssistantChange}
                  showAssistantSelector={showAssistantSelector}
                  loading={loading}
                  disabled={buttonDisabled}
                  onFilesSelected={onFilesSelected}
                  fileUploadConfig={fileUploadConfig}
                  attachedFiles={attachedFiles}
                  onFilesChange={onFilesChange}
                  fileCompatibilityTooltip={fileCompatibilityTooltip}
                  canEditModel={canEditModel}
                  onModelChange={onModelChange}
                  isModelSaving={isModelSaving}
                  isLoadingAssistants={isLoadingAssistants}
                  enableDictation
                  enableMemory
                  onExpandedChange={setComposerExpanded}
                />
              </div>
            </div>

            <div className="mx-auto w-full max-w-[720px] shrink-0 pb-10">
              <div
                data-tour="suggestions-container"
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
              >
                {isLoadingSkills
                  ? Array.from({ length: pageSize }, (_, i) => (
                      <SkillCardSkeleton key={`skeleton-${i}`} />
                    ))
                  : visibleSkills.map((skill, index) => (
                      <div
                        key={skill.id}
                        className="animate-fade-in"
                        style={{ animationDelay: `${index * 50}ms`, animationFillMode: "backwards" }}
                      >
                        <SkillCard
                          skill={skill}
                          onClick={handleSuggestionClick}
                          disabled={fileCompatibilityIssue}
                        />
                      </div>
                    ))}
              </div>

              {showPagination && (
                <div
                  data-testid="skills-pagination"
                  className="mt-5 flex items-center justify-center gap-3 animate-fade-in"
                >
                  <CarouselArrowButton
                    direction="prev"
                    onClick={goPrev}
                    disabled={page === 0}
                    ariaLabel={askT("skills.pagination.previous")}
                  />
                  <div
                    className="flex items-center gap-2"
                    role="tablist"
                    aria-label={askT("skills.pagination.pages")}
                  >
                    {Array.from({ length: totalPages }).map((_, idx) => (
                      <button
                        key={idx}
                        type="button"
                        role="tab"
                        aria-selected={page === idx}
                        aria-label={askT("skills.pagination.goToPage", { page: idx + 1 })}
                        onClick={() => setPage(idx)}
                        className={clsx(
                          "h-1.5 rounded-full transition-all duration-200",
                          page === idx
                            ? "w-5 bg-gpt-text-secondary"
                            : "w-1.5 bg-gpt-border hover:bg-gpt-text-secondary/60",
                        )}
                      />
                    ))}
                  </div>
                  <CarouselArrowButton
                    direction="next"
                    onClick={goNext}
                    disabled={page >= totalPages - 1}
                    ariaLabel={askT("skills.pagination.next")}
                  />
                </div>
              )}
            </div>
            <div className="hidden md:block md:flex-1" />
          </div>
      </FileDropZone>
    </GailGptWelcomeTour>
  );
};

export default WelcomeScreen;
